import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { isEmployeePortalRoute } from '../services/api';

const AlertContext = createContext();

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within AlertProvider');
  }
  return context;
};

// Helper function to check if user is authenticated
// We check localStorage for currentUser since AlertProvider is not inside AuthProvider
const isUserAuthenticated = () => {
  try {
    const currentUser = localStorage.getItem('currentUser');
    return currentUser && currentUser !== 'null' && currentUser !== 'undefined';
  } catch {
    return false;
  }
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const checkAlerts = async () => {
    // Don't check alerts if not authenticated
    if (!isUserAuthenticated()) {
      return;
    }

    try {
      setLoading(true);

      // Fetch monitoring alerts + HRMS in-app notifications in parallel.
      // _suppressAccessDenied: true prevents the global interceptor from showing
      // an access-denied toast when this user lacks permission for these endpoints —
      // we silently fall back to empty data instead.
      const suppressCfg = { _suppressAccessDenied: true };
      // Productivity / security alerts belong to the admin portal — never request
      // them from Employee Portal pages.
      const inEmployeePortal = isEmployeePortalRoute();
      const [productivityResponse, generalAlertsResponse, hrmsNotifResponse] = await Promise.all([
        inEmployeePortal
          ? Promise.resolve({ data: { success: true, alerts: [] } })
          : api.get('/api/productivity/alerts/check', suppressCfg).catch(() => ({ data: { success: true, alerts: [] } })),
        inEmployeePortal
          ? Promise.resolve({ data: [] })
          : api.get('/api/alerts/?status=active', suppressCfg).catch(() => ({ data: [] })),
        api.get('/api/admin-notifications?limit=30', suppressCfg).catch(() => ({ data: { notifications: [] } })),
      ]);

      // Productivity category time-limit alerts
      const productivityAlerts = productivityResponse.data.success
        ? (productivityResponse.data.alerts || [])
        : [];

      // General device / security alerts
      const generalAlerts = Array.isArray(generalAlertsResponse.data)
        ? generalAlertsResponse.data
        : (generalAlertsResponse.data?.alerts || []);

      const formattedGeneralAlerts = generalAlerts.map(alert => ({
        id: alert.id,
        category_id: alert.id,
        title: alert.title || alert.message,
        message: alert.message,
        severity: alert.severity,
        timestamp: alert.timestamp,
        type: alert.type || alert.category,
        icon: alert.icon || '',
        category: alert.category,
        metadata: alert.metadata,
      }));

      // HRMS in-app notifications (leave, loans, payroll, swipe, reimbursement)
      const hrmsRaw = hrmsNotifResponse.data?.notifications || [];
      const HRMS_ICONS = {
        leave_request: '', leave_approved: '', leave_rejected: '',
        loan_approved: '', loan_rejected: '', loan_paused: '', loan_resumed: '▶',
        payslip_ready: '', payroll_processed: '',
        reimbursement_submitted: '', reimbursement_approved: '',
        reimbursement_rejected: '', reimbursement_disbursed: '',
        swipe_submitted: '', swipe_approved: '', swipe_rejected: '',
        swipe_pending_hr: '',
      };
      const formattedHrms = hrmsRaw
        .filter(n => !n.is_dismissed)
        .map(n => ({
          id: n.notification_id,
          category_id: n.notification_id,
          title: n.title,
          message: n.message,
          severity: n.priority === 'urgent' ? 'critical' : n.priority === 'high' ? 'high' : 'medium',
          timestamp: n.created_at,
          type: 'hrms_notification',
          notification_type: n.notification_type,
          icon: HRMS_ICONS[n.notification_type] || '',
          is_read: n.is_read,
          isHrms: true,
        }));

      // Merge all and sort newest-first
      const allAlerts = [...productivityAlerts, ...formattedGeneralAlerts, ...formattedHrms].sort((a, b) => {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        return timeB - timeA;
      });

      setAlerts(allAlerts);
      setLastChecked(new Date());
    } catch (error) {
      console.error('Failed to check alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check alerts on mount and every 5 minutes (only if authenticated)
  useEffect(() => {
    // Only check if authenticated
    if (isUserAuthenticated()) {
      checkAlerts();
    }
    const interval = setInterval(() => {
      if (isUserAuthenticated()) {
        checkAlerts();
      }
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  const acknowledgeAlert = async (categoryId, alertType = 'productivity') => {
    try {
      // Determine which endpoint to call based on alert type
      if (alertType === 'productivity') {
        await api.post(`/api/productivity/alerts/acknowledge/${categoryId}`);
      } else {
        // General alerts (USB, security, etc.)
        await api.post(`/api/alerts/${categoryId}/acknowledge`);
      }
      
      // Remove from local state
      setAlerts(alerts.filter(a => a.category_id !== categoryId && a.id !== categoryId));
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      throw error;
    }
  };

  const dismissAlert = (categoryId) => {
    setAlerts(alerts.filter(a => a.category_id !== categoryId));
  };

  const clearAllAlerts = async () => {
    try {
      await api.post('/api/alerts/clear-all');
      // Also acknowledge productivity alerts locally
      setAlerts([]);
    } catch (error) {
      console.error('Failed to clear all alerts:', error);
      throw error;
    }
  };

  const value = {
    alerts,
    alertCount: alerts.length,
    loading,
    lastChecked,
    checkAlerts,
    acknowledgeAlert,
    dismissAlert,
    clearAllAlerts
  };

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};
